import React, { useState } from 'react';
import { formatAmount } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import { resolveTaxRegime } from '../../../utils/tax-regime';
import type { GstBreakupDisplayProps, GstSlab } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function makeFmt(currency: CurrencyCode) {
  return (amount: number) => formatAmount(amount, currency);
}

// Per-slab total tax — used by the single-line VAT path where there is no
// CGST/SGST/IGST split (every GCC regime).
function slabTotal(slab: GstSlab) {
  return slab.cgst + slab.sgst + slab.igst;
}

/**
 * Regime-resolved tax vocabulary shared by every variant. Under India the
 * values equal the legacy hardcoded literals (`CGST`/`SGST`/`IGST`, `GST`,
 * `GSTIN`), so the rendered output is byte-identical; every GCC regime
 * collapses to a single `{taxName}` column and the regime tax-id label.
 */
interface TaxLabels {
  single: boolean;
  taxName: string;
  cgst: string;
  sgst: string;
  igst: string;
  taxIdLabel: string | null;
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function LoadingSkeleton({ taxName }: { taxName: string }) {
  return (
    <div aria-busy="true" aria-label={`Loading ${taxName} breakup`} className="space-y-2">
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          className="block h-4 rounded bg-neutral-200 animate-pulse-soft"
          style={{ width: i === 1 ? '100%' : i === 2 ? '80%' : '60%' }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

// ─── Compact variant ──────────────────────────────────────────────────────────

function CompactView({
  slabs,
  isInterState,
  totalTax,
  labels,
  fmt,
}: {
  slabs: GstSlab[];
  isInterState: boolean;
  totalTax: number;
  labels: TaxLabels;
  fmt: (amount: number) => string;
}) {
  const activeSlabs = slabs.filter((s) => s.taxableAmount > 0);

  return (
    <div className="text-sm text-neutral-700 space-y-0.5">
      {activeSlabs.map((slab) => {
        const halfRate = slab.rate / 2;
        // Same-state India: CGST and SGST each get their own line, like the
        // saved bill, so the odd paisa on CGST shows (QA GP1-001).
        if (!labels.single && !isInterState) {
          return (
            <React.Fragment key={slab.rate}>
              <div className="flex justify-between tabular-nums">
                <span>
                  {labels.cgst} @{halfRate}%
                </span>
                <span>{fmt(slab.cgst)}</span>
              </div>
              <div className="flex justify-between tabular-nums">
                <span>
                  {labels.sgst} @{halfRate}%
                </span>
                <span>{fmt(slab.sgst)}</span>
              </div>
            </React.Fragment>
          );
        }
        return (
          <div key={slab.rate} className="flex justify-between tabular-nums">
            {labels.single ? (
              <span>
                {labels.taxName} @{slab.rate}%
              </span>
            ) : (
              <span>
                {labels.igst} @{slab.rate}%
              </span>
            )}
            <span>{fmt(labels.single ? slabTotal(slab) : slab.igst)}</span>
          </div>
        );
      })}
      <div className="flex justify-between font-medium border-t border-neutral-200 pt-1 mt-1 tabular-nums">
        <span>Total Tax</span>
        <span>{fmt(totalTax)}</span>
      </div>
    </div>
  );
}

// ─── Detailed variant ────────────────────────────────────────────────────────

function DetailedView({
  slabs,
  isInterState,
  totalTax,
  labels,
  fmt,
}: {
  slabs: GstSlab[];
  isInterState: boolean;
  totalTax: number;
  labels: TaxLabels;
  fmt: (amount: number) => string;
}) {
  const [expandedRate, setExpandedRate] = useState<number | null>(null);
  const activeSlabs = slabs.filter((s) => s.taxableAmount > 0);
  // Single-line VAT and IGST both render one tax column; only the intra-state
  // India path splits into two (CGST + SGST).
  const oneCol = labels.single || isInterState;

  function toggleSlab(rate: number) {
    setExpandedRate((prev) => (prev === rate ? null : rate));
  }

  return (
    <div className="text-sm">
      <table className="w-full border-collapse" role="table">
        <thead>
          <tr className="text-xs text-neutral-500 border-b border-neutral-200">
            <th scope="col" className="text-left py-1.5 font-medium">
              {labels.taxName} Rate
            </th>
            <th scope="col" className="text-right py-1.5 font-medium">
              Taxable Amt
            </th>
            {labels.single ? (
              <th scope="col" className="text-right py-1.5 font-medium">
                {labels.taxName}
              </th>
            ) : isInterState ? (
              <th scope="col" className="text-right py-1.5 font-medium">
                {labels.igst}
              </th>
            ) : (
              <>
                <th scope="col" className="text-right py-1.5 font-medium">
                  {labels.cgst}
                </th>
                <th scope="col" className="text-right py-1.5 font-medium">
                  {labels.sgst}
                </th>
              </>
            )}
            <th scope="col" className="text-right py-1.5 font-medium">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {activeSlabs.map((slab) => {
            const isExpanded = expandedRate === slab.rate;
            const taxTotal = labels.single
              ? slabTotal(slab)
              : isInterState
                ? slab.igst
                : slab.cgst + slab.sgst;

            return (
              <React.Fragment key={slab.rate}>
                <tr
                  className="border-b border-neutral-100 cursor-pointer hover:bg-neutral-50 tabular-nums"
                  onClick={() => toggleSlab(slab.rate)}
                  role="row"
                  aria-expanded={isExpanded}
                >
                  <td className="py-1.5 text-left">
                    <span className="flex items-center gap-1">
                      <svg
                        className={[
                          'w-3 h-3 text-neutral-400 transition-transform',
                          isExpanded ? 'rotate-90' : '',
                        ].join(' ')}
                        viewBox="0 0 12 12"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M4.5 2.5l3 3.5-3 3.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {slab.rate}%
                    </span>
                  </td>
                  <td className="py-1.5 text-right">{fmt(slab.taxableAmount)}</td>
                  {labels.single ? (
                    <td className="py-1.5 text-right">{fmt(slabTotal(slab))}</td>
                  ) : isInterState ? (
                    <td className="py-1.5 text-right">{fmt(slab.igst)}</td>
                  ) : (
                    <>
                      <td className="py-1.5 text-right">{fmt(slab.cgst)}</td>
                      <td className="py-1.5 text-right">{fmt(slab.sgst)}</td>
                    </>
                  )}
                  <td className="py-1.5 text-right font-medium">{fmt(taxTotal)}</td>
                </tr>
                {isExpanded && slab.items.length > 0 && (
                  <tr role="row">
                    <td colSpan={oneCol ? 3 : 4} className="pb-2 pl-5">
                      <ul className="text-xs text-neutral-500 space-y-0.5 pt-1">
                        {slab.items.map((item, idx) => (
                          <li key={idx} className="flex justify-between tabular-nums">
                            <span>{item.name}</span>
                            <span>{fmt(item.amount)}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td />
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="font-medium text-neutral-800 tabular-nums border-t border-neutral-300">
            <td className="py-2 text-left" colSpan={oneCol ? 2 : 3}>
              Total Tax
            </td>
            <td className="py-2 text-right" colSpan={oneCol ? 1 : 2}>
              {fmt(totalTax)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ─── Invoice variant ──────────────────────────────────────────────────────────

function InvoiceView({
  slabs,
  isInterState,
  totalTax,
  sellerGstin,
  labels,
  fmt,
}: {
  slabs: GstSlab[];
  isInterState: boolean;
  totalTax: number;
  sellerGstin?: string;
  labels: TaxLabels;
  fmt: (amount: number) => string;
}) {
  const activeSlabs = slabs.filter((s) => s.taxableAmount > 0);
  const oneCol = labels.single || isInterState;

  return (
    <div className="text-xs font-mono">
      {sellerGstin && labels.taxIdLabel && (
        <p className="mb-2 text-neutral-600">
          <span className="font-semibold">{labels.taxIdLabel}:</span> {sellerGstin}
        </p>
      )}
      <table className="w-full border border-neutral-300 border-collapse" role="table">
        <thead>
          <tr className="bg-neutral-100 text-neutral-700">
            <th scope="col" className="border border-neutral-300 px-2 py-1 text-left">
              {labels.taxName} %
            </th>
            <th scope="col" className="border border-neutral-300 px-2 py-1 text-right">
              Taxable Value
            </th>
            {labels.single ? (
              <th scope="col" className="border border-neutral-300 px-2 py-1 text-right">
                {labels.taxName} Amt
              </th>
            ) : isInterState ? (
              <th scope="col" className="border border-neutral-300 px-2 py-1 text-right">
                {labels.igst} Amt
              </th>
            ) : (
              <>
                <th scope="col" className="border border-neutral-300 px-2 py-1 text-right">
                  {labels.cgst} Amt
                </th>
                <th scope="col" className="border border-neutral-300 px-2 py-1 text-right">
                  {labels.sgst} Amt
                </th>
              </>
            )}
            <th scope="col" className="border border-neutral-300 px-2 py-1 text-right">
              Total Tax
            </th>
          </tr>
        </thead>
        <tbody>
          {activeSlabs.map((slab) => {
            const taxTotal = labels.single
              ? slabTotal(slab)
              : isInterState
                ? slab.igst
                : slab.cgst + slab.sgst;
            return (
              <tr key={slab.rate} className="tabular-nums">
                <td className="border border-neutral-300 px-2 py-1">{slab.rate}%</td>
                <td className="border border-neutral-300 px-2 py-1 text-right">
                  {fmt(slab.taxableAmount)}
                </td>
                {labels.single ? (
                  <td className="border border-neutral-300 px-2 py-1 text-right">
                    {fmt(slabTotal(slab))}
                  </td>
                ) : isInterState ? (
                  <td className="border border-neutral-300 px-2 py-1 text-right">
                    {fmt(slab.igst)}
                  </td>
                ) : (
                  <>
                    <td className="border border-neutral-300 px-2 py-1 text-right">
                      {fmt(slab.cgst)}
                    </td>
                    <td className="border border-neutral-300 px-2 py-1 text-right">
                      {fmt(slab.sgst)}
                    </td>
                  </>
                )}
                <td className="border border-neutral-300 px-2 py-1 text-right font-semibold">
                  {fmt(taxTotal)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-neutral-50 font-semibold tabular-nums">
            <td
              className="border border-neutral-300 px-2 py-1 text-left"
              colSpan={oneCol ? 2 : 3}
            >
              Total
            </td>
            <td
              className="border border-neutral-300 px-2 py-1 text-right"
              colSpan={oneCol ? 1 : 2}
            >
              {fmt(totalTax)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ─── GstBreakupDisplay ────────────────────────────────────────────────────────

export function GstBreakupDisplay({
  slabs,
  isInterState = false,
  totalTax,
  sellerGstin,
  variant = 'compact',
  loading = false,
  taxRegime,
  currency = 'INR',
  className,
}: GstBreakupDisplayProps) {
  // Hook-free: default to the India (INR) regime so existing call sites are
  // byte-identical; consumers thread the resolved regime via `useTaxRegime()`.
  const regime = taxRegime ?? resolveTaxRegime('INR');
  const fmt = makeFmt(currency);
  const split = regime.splitLabels;
  const labels: TaxLabels = {
    single: split === null,
    taxName: regime.taxName,
    cgst: split ? split[0] : regime.taxName,
    sgst: split ? split[1] : regime.taxName,
    igst: split ? split[2] : regime.taxName,
    taxIdLabel: regime.taxIdLabel,
  };

  if (loading) {
    return (
      <div className={className}>
        <LoadingSkeleton taxName={labels.taxName} />
      </div>
    );
  }

  return (
    <div className={className}>
      {variant === 'compact' && (
        <CompactView
          slabs={slabs}
          isInterState={isInterState}
          totalTax={totalTax}
          labels={labels}
          fmt={fmt}
        />
      )}
      {variant === 'detailed' && (
        <DetailedView
          slabs={slabs}
          isInterState={isInterState}
          totalTax={totalTax}
          labels={labels}
          fmt={fmt}
        />
      )}
      {variant === 'invoice' && (
        <InvoiceView
          slabs={slabs}
          isInterState={isInterState}
          totalTax={totalTax}
          sellerGstin={sellerGstin}
          labels={labels}
          fmt={fmt}
        />
      )}
    </div>
  );
}
